import { useFormContext } from 'react-hook-form';
import { useEffect, useState, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, Edit3, Package, Search, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getDispatchRate } from '@/services/deliveryService';
import { DeliveryInfo, AdditionalJob, DeliveryBilling as DeliveryBillingType } from '@/types/delivery.types';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';

// Billing rate constants
const DEFAULT_BASE_CHARGE = 150.0;
const FUEL_SURCHARGE_RATE = 0.1; // 10%
const BOX_FEE_PER_ITEM = 18.0;
const COPY_CHARGE_PER_PAGE = 0.2;
const RETURN_AMOUNT_RATE = 0.5; // 50% of base
const CHECK_ADVANCEMENT_FEE = 2.0;

/**
 * Fetch dispatch rate from API using county IDs
 */
const fetchDispatchRate = async (pickupCountyId: string, deliveryCountyId: string): Promise<number> => {
  try {
    const result = await getDispatchRate(pickupCountyId, deliveryCountyId);
    return result.data?.baseCharge || DEFAULT_BASE_CHARGE;
  } catch (error) {
    console.error('Error fetching dispatch rate:', error);
    return DEFAULT_BASE_CHARGE;
  }
};

interface BillingCalculation extends DeliveryBillingType {
  index: number;
}

const BILLING_FIELDS = [
  { key: 'baseCharge', label: 'Base Fee', code: 'Base Fee' },
  { key: 'fuelSurcharge', label: 'Fuel Surcharge', code: 'FS' },
  { key: 'returnAmount', label: 'Return Fee', code: 'RC' },
  { key: 'boxFee', label: 'Boxes', code: 'BOXES' },
  { key: 'copyCharges', label: 'Copy Charges', code: 'CC' },
  { key: 'checkAdvancementFee', label: 'Check Advancement Fee', code: 'CAF' },
  { key: 'miscFee', label: 'Misc Fee / Prepaid', code: 'MISC' },
] as const;

export const DeliveryBilling = ({ isEditMode = false }: { isEditMode?: boolean }) => {
  const { control, watch, setValue, getValues } = useFormContext();
  const [billingCalculations, setBillingCalculations] = useState<BillingCalculation[]>([]);
  const [selectedDeliveryIndex, setSelectedDeliveryIndex] = useState(0);
  const [editingItem, setEditingItem] = useState<{ index: number; key: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [billingSearch, setBillingSearch] = useState('');
  const [manuallyAddedOrder, setManuallyAddedOrder] = useState<string[]>([]);
  const hasInitializedRef = useRef(false);

  const deliveries = (watch('deliveries') || []) as DeliveryInfo[];
  const pickupCounty = watch('pickupCounty') as string;
  const pickupType = watch('pickupType') as string;
  const numberOfItems = (watch('numberOfItems') || 0) as number;
  const additionalJobs = (watch('additionalJobs') || []) as AdditionalJob[];

  // Ref to track previous dependency values to prevent infinite loops
  const prevDepsRef = useRef<string>('');

  // Calculate billing for each delivery
  useEffect(() => {
    const calculateBilling = async () => {
      const currentDepsKey = JSON.stringify({
        pickupCounty,
        pickupType,
        numberOfItems,
        additionalJobs,
        deliveries: deliveries.map((d) => ({
          deliveryCounty: d.deliveryCounty,
          hasDgrCheck: d.hasDgrCheck,
          id: d.id,
          hasReturn: d.hasReturn
        })),
      });

      if (prevDepsRef.current === currentDepsKey) {
        return;
      }
      prevDepsRef.current = currentDepsKey;

      const calculations = await Promise.all(
        deliveries.map(async (delivery, index) => {
          const deliveryCounty = delivery.deliveryCounty;

          let baseCharge = DEFAULT_BASE_CHARGE;
          if (pickupCounty && deliveryCounty) {
            baseCharge = await fetchDispatchRate(pickupCounty, deliveryCounty);
          }

          const fuelSurcharge = baseCharge * FUEL_SURCHARGE_RATE;

          let boxFee = 0;
          if (pickupType === 'BOX') {
            boxFee = BOX_FEE_PER_ITEM * numberOfItems;
          }

          let copyCharges = 0;
          const printingJob = additionalJobs.find((job) => job.type === 'PRINTING');
          if (printingJob && printingJob.numberOfPages) {
            copyCharges = COPY_CHARGE_PER_PAGE * printingJob.numberOfPages;
          }

          let returnAmount = 0;
          if (delivery.hasReturn) {
            returnAmount = baseCharge * RETURN_AMOUNT_RATE;
          }

          let checkAdvancementFee = 0;
          if (delivery.hasDgrCheck) {
            checkAdvancementFee = CHECK_ADVANCEMENT_FEE;
          }

          // Preserve manual misc fee
          const currentVals = getValues(`deliveryBillings.${index}`) as DeliveryBillingType | undefined;
          const miscFee = currentVals?.miscFee || 0;

          const totalBillingFee = baseCharge + fuelSurcharge + boxFee + copyCharges + returnAmount + checkAdvancementFee + miscFee;

          return {
            index,
            baseCharge,
            fuelSurcharge,
            boxFee,
            copyCharges,
            returnAmount,
            checkAdvancementFee,
            miscFee,
            totalBillingFee,
          };
        })
      );

      setBillingCalculations(calculations);

      const isFirstRun = !hasInitializedRef.current;
      hasInitializedRef.current = true;

      calculations.forEach((calc) => {
        const currentVals = getValues(`deliveryBillings.${calc.index}`) as DeliveryBillingType | undefined;

        // In edit mode on first run, skip fields that already have saved values.
        const skip = (savedVal: number | undefined) => isEditMode && isFirstRun && savedVal != null && savedVal > 0;

        if (!skip(currentVals?.baseCharge) && currentVals?.baseCharge !== calc.baseCharge) setValue(`deliveryBillings.${calc.index}.baseCharge`, calc.baseCharge, { shouldValidate: true });
        if (!skip(currentVals?.fuelSurcharge) && currentVals?.fuelSurcharge !== calc.fuelSurcharge) setValue(`deliveryBillings.${calc.index}.fuelSurcharge`, calc.fuelSurcharge, { shouldValidate: true });
        if (!skip(currentVals?.boxFee) && currentVals?.boxFee !== calc.boxFee) setValue(`deliveryBillings.${calc.index}.boxFee`, calc.boxFee, { shouldValidate: true });
        if (!skip(currentVals?.copyCharges) && currentVals?.copyCharges !== calc.copyCharges) setValue(`deliveryBillings.${calc.index}.copyCharges`, calc.copyCharges, { shouldValidate: true });
        if (!skip(currentVals?.returnAmount) && currentVals?.returnAmount !== calc.returnAmount) setValue(`deliveryBillings.${calc.index}.returnAmount`, calc.returnAmount, { shouldValidate: true });
        if (!skip(currentVals?.checkAdvancementFee) && currentVals?.checkAdvancementFee !== calc.checkAdvancementFee) setValue(`deliveryBillings.${calc.index}.checkAdvancementFee`, calc.checkAdvancementFee, { shouldValidate: true });
        if (!skip(currentVals?.miscFee) && currentVals?.miscFee !== calc.miscFee) setValue(`deliveryBillings.${calc.index}.miscFee`, calc.miscFee, { shouldValidate: true });

        recalculateTotal(calc.index);
      });
    };

    if (deliveries.length > 0) {
      calculateBilling();
    }
  }, [deliveries, pickupCounty, pickupType, numberOfItems, additionalJobs, setValue, getValues]);

  const recalculateTotal = (index: number) => {
    const vals = getValues(`deliveryBillings.${index}`) || {};
    const total = BILLING_FIELDS.reduce((acc, field) => {
      const val = parseFloat(vals[field.key] || '0');
      if (field.key !== 'fuelSurcharge') return acc + val;
      // Fuel surcharge logic: if base charge changes, should we update fuel surcharge?
      // In this function, we just sum up. distinct handler for base charge update.
      return acc;
    }, 0);

    // Actually simplicity: just sum all fields
    const sum = BILLING_FIELDS.reduce((acc, field) => acc + parseFloat(vals[field.key] || '0'), 0);
    setValue(`deliveryBillings.${index}.totalBillingFee`, sum, { shouldValidate: true });
  };

  const handleUpdateAmount = (index: number, key: string, value: number) => {
    setValue(`deliveryBillings.${index}.${key}`, value, { shouldValidate: true });

    if (key === 'baseCharge') {
      const fuelSurcharge = value * FUEL_SURCHARGE_RATE;
      setValue(`deliveryBillings.${index}.fuelSurcharge`, parseFloat(fuelSurcharge.toFixed(2)), { shouldValidate: true });

      const currentReturnAmount = getValues(`deliveryBillings.${index}.returnAmount`) as number;
      if (currentReturnAmount > 0) {
        setValue(`deliveryBillings.${index}.returnAmount`, parseFloat((value * RETURN_AMOUNT_RATE).toFixed(2)), { shouldValidate: true });
      }
    }

    recalculateTotal(index);
    setEditingItem(null);
  };

  const handleDeleteItem = (index: number, key: string) => {
    setValue(`deliveryBillings.${index}.${key}`, 0, { shouldValidate: true });
    recalculateTotal(index);
    setManuallyAddedOrder(prev => prev.filter(k => k !== key));
  };

  // Prepare view model for current delivery
  const currentDeliveryIndex = selectedDeliveryIndex;
  const currentBillingValues = (watch(`deliveryBillings.${currentDeliveryIndex}`) || {}) as Record<string, number>;

  // Auto-calculated items: in BILLING_FIELDS order, excluding manually added ones
  const autoItems = BILLING_FIELDS
    .filter(f => !manuallyAddedOrder.includes(f.key))
    .map(f => ({ ...f, amount: parseFloat(String(currentBillingValues[f.key] || 0)) }))
    .filter(item => item.amount > 0);

  // Manually added items: in the order they were added
  const manualItems = manuallyAddedOrder
    .map(key => BILLING_FIELDS.find(f => f.key === key))
    .filter((f): f is typeof BILLING_FIELDS[number] => f != null)
    .map(f => ({ ...f, amount: parseFloat(String(currentBillingValues[f.key] || 0)) }))
    .filter(item => item.amount > 0);

  const displayItems = [...autoItems, ...manualItems].map((item, idx) => ({ ...item, sn: idx + 1 }));

  const availableOptions = BILLING_FIELDS.filter(field => {
    const val = parseFloat(String(currentBillingValues[field.key] || 0));
    return val <= 0;
  });

  // Calculate Grand Total
  // We sum the 'totalBillingFee' of all deliveries
  const allBillings = watch('deliveryBillings') as DeliveryBillingType[] || [];
  const grandTotal = allBillings.reduce((acc, b) => acc + (b?.totalBillingFee || 0), 0);

  return (
    <div className="flex flex-col w-full mt-6 gap-6 relative min-h-[600px]">
      {/* Tabs / Delivery Select */}
      {deliveries.length > 0 && (
        <div className="flex w-full items-center border-b border-[#cac8c8] bg-[#f5f5f5]">
          {deliveries.map((delivery, index) => {
            const isSelected = selectedDeliveryIndex === index;
            return (
              <button
                type="button"
                key={delivery.id}
                onClick={() => setSelectedDeliveryIndex(index)}
                className={cn(
                  "flex items-center gap-2 px-4 py-3 min-w-[140px] border-r border-[#cac8c8] text-sm font-medium transition-colors",
                  isSelected
                    ? "bg-[#edf5ff] text-[#0a2240]"
                    : "bg-[#f5f5f5] text-[#0a2240] hover:bg-gray-100"
                )}
              >
                <Package className="h-5 w-5 text-[#0a2240]" />
                <span className="truncate max-w-[150px]">{delivery.personEntityName || 'Unnamed'}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Main Billing Table Card */}
      <Card className="rounded-md border border-[#cac8c8] shadow-none">
        <div className="p-6">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-[#0a0a0a]">Billing Information</h3>
          </div>

          <div className="rounded-md border border-[#f5f5f5]">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#cac8c8] hover:bg-transparent">
                  <TableHead className="w-[60px] text-[#0a0a0a] font-medium">SN</TableHead>
                  <TableHead className="w-[100px] text-[#0a0a0a] font-medium">Code</TableHead>
                  <TableHead className="text-[#0a0a0a] font-medium">Description</TableHead>
                  <TableHead className="text-right w-[150px] text-[#0a0a0a] font-medium">Amount</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                      No billing items. Add one below.
                    </TableCell>
                  </TableRow>
                ) : (
                  displayItems.map((item) => (
                    <TableRow key={item.key} className="border-b border-[#f5f5f5] hover:bg-transparent">
                      <TableCell className="font-normal text-[#0a0a0a]">{item.sn}.</TableCell>
                      <TableCell className="font-normal text-[#0a0a0a]">{item.code}</TableCell>
                      <TableCell className="font-normal text-[#0a0a0a]">{item.label}</TableCell>
                      <TableCell className="text-right font-normal text-[#0a0a0a]">
                        {editingItem?.index === currentDeliveryIndex && editingItem?.key === item.key ? (
                          <Input
                            autoFocus
                            type="number"
                            className="h-8 w-full text-right"
                            defaultValue={item.amount}
                            onBlur={(e) => handleUpdateAmount(currentDeliveryIndex, item.key, parseFloat(e.target.value))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleUpdateAmount(currentDeliveryIndex, item.key, parseFloat(e.currentTarget.value));
                              }
                            }}
                          />
                        ) : (
                          formatCurrency(item.amount)
                        )}
                      </TableCell>
                      <TableCell className="flex justify-end items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleDeleteItem(currentDeliveryIndex, item.key)}
                          className="text-[#D72222] hover:text-[#D72222]/80 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingItem({ index: currentDeliveryIndex, key: item.key });
                          }}
                          className="text-[#0A2240] hover:text-[#0A2240]/80 transition-colors"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}

                {/* Search Input Row (Visual Design matching) */}
                <TableRow className="border-none hover:bg-transparent">
                  <TableCell colSpan={5} className="p-4">
                    <p className="text-sm font-medium text-[#0a0a0a] mb-2">Add Billing Items</p>
                    <Popover open={isAddOpen} onOpenChange={(open) => { setIsAddOpen(open); if (!open) setBillingSearch(''); }}>
                      <PopoverTrigger asChild>
                        <div className="flex items-center w-full max-w-md border border-[#cac8c8] rounded-md px-3 py-2 cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                          <Search className="h-4 w-4 text-[#0a0a0a] mr-2" />
                          <span className="text-sm text-[#676767] flex-1">Select Billing Items</span>
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[400px]" align="start">
                        <div className="p-2 border-b border-[#cac8c8]">
                          <input
                            autoFocus
                            placeholder="Search billing items..."
                            value={billingSearch}
                            onChange={(e) => setBillingSearch(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm outline-none bg-transparent"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto py-1">
                          {availableOptions.filter((f) =>
                            `${f.label} ${f.code}`.toLowerCase().includes(billingSearch.toLowerCase())
                          ).length === 0 ? (
                            <p className="px-3 py-2 text-sm text-[#676767]">No items found.</p>
                          ) : (
                            availableOptions
                              .filter((f) =>
                                `${f.label} ${f.code}`.toLowerCase().includes(billingSearch.toLowerCase())
                              )
                              .map((field) => (
                                <button
                                  key={field.key}
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#EDF5FF] cursor-pointer"
                                  onClick={() => {
                                    let defaultVal = 0.00;
                                    if (field.key === 'boxFee') defaultVal = BOX_FEE_PER_ITEM * numberOfItems;
                                    if (field.key === 'returnAmount') defaultVal = (billingCalculations[currentDeliveryIndex]?.baseCharge ?? DEFAULT_BASE_CHARGE) * RETURN_AMOUNT_RATE;

                                    handleUpdateAmount(currentDeliveryIndex, field.key, defaultVal || 1.00);
                                    setManuallyAddedOrder(prev => [...prev, field.key]);
                                    setIsAddOpen(false);
                                    setBillingSearch('');
                                    setTimeout(() => setEditingItem({ index: currentDeliveryIndex, key: field.key }), 100);
                                  }}
                                >
                                  {field.label} ({field.code})
                                </button>
                              ))
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </Card>

      {/* Global Total */}
      <div className="flex flex-col gap-2 mt-4 max-w-md">
        <label className="text-sm font-medium text-black">Total Billing Amount</label>
        <div className="bg-[#f5f5f5] border border-[#cac8c8] rounded-md px-3 py-3 text-sm text-[#0a0a0a]">
          {formatCurrency(grandTotal)}
        </div>
      </div>
    </div>
  );
};

